import {
    Avatar,
    Button,
    Flex,
    Image,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    VStack,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    HStack,
    Badge,
} from "@chakra-ui/react";
import Template from "../../../components/Layout/Template";
import { colors } from "../../../styles";
import MyIcon from "../../../components/Icon/MyIcon";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IProfile, IUserType } from "../../../types";
import {
    getProfile,
    getProfileChats,
    getProfileCommentedOnChats,
    toggleBlockProfile,
} from "../../../api";
import { typeToKor } from "../../../utils";
import { useEffect, useState } from "react";
import ConfirmAlert, { useConfirmAlert } from "../../../components/Chat/ConfirmAlert";
import ProfileChatTabPanel from "./ProfileChatTabPanel";
import { useAuthStore } from "../../../store";

export default function Profile() {

    const navigate = useNavigate();

    /* params */

    const { nickname } = useParams();

    /* query */

    const queryClient = useQueryClient();
    const { showConfirm, confirmAlertProps } = useConfirmAlert();

    const { data: profileData } = useQuery<IProfile>({
        queryKey: ["getProfile", nickname],
        queryFn: () => getProfile({ nickname: nickname! }),
        enabled: !!nickname,
    });

    const isBlocking = profileData?.isBlocking;
    const isBlocked = profileData?.isBlocked;

    const visibility = profileData?.visibility;

    const tabSelectedStyle = {
        color: colors.blue,
        fontWeight: 700,
        borderBottom: `2px solid ${colors.blue}`,
    };

    /* tab state */

    const [tabIndex, setTabIndex] = useState(0);
    const [visitedTabs, setVisitedTabs] = useState<Set<number>>(() => new Set([0]));

    const handleTabChange = (index: number) => {
        setTabIndex(index);
        setVisitedTabs((prev) => new Set(prev).add(index));
    };

    /* toggleBlockProfileMutation */

    const toggleBlockProfileMutation = useMutation({
        mutationFn: toggleBlockProfile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getProfile", nickname] });
            queryClient.invalidateQueries({ queryKey: ["chats"] });
            queryClient.invalidateQueries({ queryKey: ["getBlockingProfiles"] });
        },
        onError: (error) => {
            console.error(error);
        },
    });

    /* render */

    return (
        <Template>
            {profileData ? (
                <VStack width={"100%"} alignItems={"center"}>
                    <Stack
                        width={{
                            base: "100%",
                            sm: "100%",
                            md: "100%",
                            lg: "80%",
                            xl: "80%",
                        }}
                        spacing={0}
                        paddingY={5}
                    >

                        {/* 프로필 영역 */}

                        <Flex
                            width={"100%"}
                            justifyContent={"space-between"}
                            alignItems={"flex-start"}
                        >
                            <VStack alignItems={"flex-start"} spacing={1}>
                                <HStack
                                    width={"100%"}
                                    justifyContent={"flex-start"}
                                    alignItems={"center"}
                                    spacing={2}
                                >
                                    <Text fontSize={24} fontWeight={700} color={colors.g1}>
                                        {profileData?.nickname}
                                    </Text>

                                    {profileData?.isMe && (
                                        <Badge
                                            backgroundColor={colors.blue}
                                            color={colors.white}
                                            borderRadius="full"
                                            paddingX={1}
                                            paddingY={0.5}
                                            fontSize={8}
                                            fontWeight={500}
                                        >
                                            나
                                        </Badge>
                                    )}
                                </HStack>
                                <Text fontSize={14} fontWeight={500} color={colors.g3}>
                                    {typeToKor(profileData?.user.type as IUserType | null)}
                                </Text>
                            </VStack>

                            {profileData?.avatar ? (
                                <Image
                                    src={profileData?.avatar}
                                    alt={`${profileData?.nickname} 프로필 이미지`}
                                    boxSize={{ base: 20, sm: 24 }}
                                    borderRadius={"full"}
                                    objectFit={"cover"}
                                    flexShrink={0}
                                />
                            ) : (
                                <Image 
                                    src={"/png/profile.png"}
                                    alt="프로필 이미지"
                                    boxSize={{ base: 20, sm: 24 }}
                                    borderRadius="full"
                                    objectFit="cover"
                                    flexShrink={0}
                                />
                            )}
                        </Flex>

                        {/* bio 영역 */}

                        {profileData?.bio && (
                            <Text
                                marginTop={4}
                                fontSize={15}
                                fontWeight={400}
                                color={colors.g1}
                                whiteSpace={"pre-wrap"}
                            >
                                {profileData?.bio}
                            </Text>
                        )}

                        {/* 메뉴 영역 */}

                        {(isBlocked || isBlocking) ? (
                            <VStack 
                                width={"100%"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                marginTop={12}
                            >
                                <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"} paddingY={10}>
                                    프로필을 볼 수 없습니다.
                                </Text>
                            </VStack>
                        ) : 
                            visibility === "PRIVATE" ? (
                                <VStack 
                                    width={"100%"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    marginTop={12}
                                >
                                    <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"} paddingY={10}>
                                        비공개 프로필입니다.
                                    </Text>
                                </VStack>
                            ) : (
                            <Tabs
                                isFitted
                                width={"100%"}
                                marginTop={6}
                                index={tabIndex}
                                onChange={handleTabChange}
                            >
                                <TabList borderBottom={`1px solid ${colors.g5}`}>
                                    <Tab _selected={tabSelectedStyle} paddingY={3} fontSize={14} color={colors.g3}>
                                        글
                                    </Tab>
                                    <Tab _selected={tabSelectedStyle} paddingY={3} fontSize={14} color={colors.g3}>
                                        댓글단 글
                                    </Tab>
                                </TabList>

                                <TabPanels>

                                    <TabPanel paddingX={0} paddingTop={10}>
                                        <ProfileChatTabPanel
                                            queryKey={["getProfileChats", nickname]}
                                            queryFn={(page, size) => getProfileChats({ nickname: nickname!, page, size })}
                                            enabled={visitedTabs.has(0) && !!nickname}
                                            emptyMessage="게시글이 없습니다."
                                        />
                                    </TabPanel>

                                    <TabPanel paddingX={0} paddingTop={10}>
                                        <ProfileChatTabPanel
                                            queryKey={["getProfileCommentedOnChats", nickname]}
                                            queryFn={(page, size) => getProfileCommentedOnChats({ nickname: nickname!, page, size })}
                                            enabled={visitedTabs.has(1) && !!nickname}
                                            emptyMessage="댓글 단 게시글이 없습니다."
                                        />
                                    </TabPanel>

                                </TabPanels>
                            </Tabs>
                        )}

                        <VStack
                            position={"fixed"}
                            right={{ base: 4, sm: 8, md: 12 }}
                            bottom={{ base: 4, sm: 8, md: 12 }}
                            spacing={{ base: 3, sm: 4, md: 4 }}
                        >
                            <Menu>
                                <MenuButton
                                    as={IconButton}
                                    aria-label='Options'
                                    icon={
                                    profileData?.isMe ? 
                                        <MyIcon src={"/svg/more.svg"} alt={"더보기"} width={20} height={20} /> 
                                        : 
                                        <MyIcon src={"/svg/more-blue.svg"} alt={"더보기"} width={20} height={20} />
                                    }
                                    variant='ghost'
                                    boxSize={{ base: 12, sm: 16, md: 20 }}
                                    minWidth={{ base: 12, sm: 16, md: 20 }}
                                    borderRadius={"full"}
                                    backgroundColor={profileData?.isMe ? colors.blue : colors.white}
                                    boxShadow={"lg"}
                                />
                                    <MenuList>
                                        {profileData?.isMe ? (
                                            <MenuItem 
                                                as={Link} 
                                                to={`/chat/profile/my`}
                                            >
                                                마이페이지로 가기
                                            </MenuItem>
                                        ) : (
                                            <MenuItem
                                                onClick={() =>
                                                    showConfirm({
                                                        message: profileData?.isBlocking
                                                            ? "차단을 해제하겠습니까?"
                                                            : "이 사용자를 차단하겠습니까?",
                                                        confirmLabel: profileData?.isBlocking ? "해제" : "차단",
                                                        onConfirm: () =>
                                                            toggleBlockProfileMutation.mutate({
                                                                nickname: nickname!,
                                                            }),
                                                    })
                                                }
                                                disabled={toggleBlockProfileMutation.isPending}
                                            >
                                                {profileData?.isBlocking ? "차단 해제" : "이 프로필 차단하기"}
                                            </MenuItem>
                                        )}
                                    </MenuList>
                            </Menu>
                        </VStack>
                    </Stack>
                </VStack>
            ) : (
                <VStack 
                    width={"100%"}
                    height={"50vh"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    spacing={4}
                >
                    <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"}>
                        프로필이 존재하지 않습니다. 
                    </Text>
                </VStack>
            )}

            <ConfirmAlert
                {...confirmAlertProps}
                isLoading={toggleBlockProfileMutation.isPending}
            />
        </Template>
    );
}
